import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { AuthCard } from '@components/common/AuthCard/AuthCard';
import { CustomTextField } from '@components/common/CustomTextField/CustomTextField';
import { CustomAlert } from '@components/common/CustomAlert/CustomAlert';
import { CustomLink } from '@components/common/CustomLink/CustomLink';
import { PrimaryButton } from '@components/buttons/PrimaryButton';
import { ThemeToggle } from '@components/common/ThemeToggle/ThemeToggle';
import { useTheme } from '@mui/material';
import { useAppTheme } from '@contexts/ThemeContext';
import { useAuth } from '@store/authStore';
import { API_ENDPOINTS } from '@services/database';
import httpClient from '@utils/httpClient';
import { getDefaultRouteForRole } from '@config/roles';
import { resetAllStores, hasSessionConflict, getLastActiveUserId } from '@store/storeResetUtils';

interface LoginCredentials {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [authenticationStarted, setAuthenticationStarted] = useState(false);

  const navigate = useNavigate();
  const { isLoading, login, isAuthenticated, user } = useAuth();
  const { isDarkMode } = useAppTheme();
  const theme = useTheme();

  // Navigate to appropriate dashboard when authenticated (only after successful login)
  useEffect(() => {
    if (isAuthenticated && user && isLoggedIn) {
      const defaultRoute = getDefaultRouteForRole(user.role);
      console.log(`Login: User successfully logged in as ${user.role}, navigating to ${defaultRoute}`);
      navigate(defaultRoute, { replace: true });
    }
  }, [isAuthenticated, user, isLoggedIn, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('Login: Form submitted with', formData);

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setError('');

    // Clear any existing mock data
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');

    try {
      setIsLoggingIn(true);
      setAuthenticationStarted(true);
      setError('');

      console.log('🔐 Login: Attempting login with backend API...');
      console.log('📡 Login: API URL:', API_ENDPOINTS.LOGIN);

      // Call the real backend API
      const data = await httpClient.post('/auth/login', {
        email: formData.email,
        password: formData.password
      });
      console.log('📄 Login Data:', data);
      console.log('📄 Backend user data:', data.user);
      console.log('📄 Profile photo from backend:', data.user?.profile_photo);
      console.log('📄 Contact from backend:', data.user?.contact);
      console.log('📄 Flag daily update from backend:', data.user?.flag_daily_update);
      console.log('📄 Full user object from backend:', JSON.stringify(data.user, null, 2));

      if (data.success) {
        // Login successful - use real user data from backend
        const profilePhoto = data.user.profile_photo
          ? (data.user.profile_photo.startsWith('http')
            ? data.user.profile_photo
            : `http://localhost:5000${data.user.profile_photo}`)
          : '';

        console.log('🖼️ Converted profile photo URL:', profilePhoto);

        // Check for session conflict before login
        const lastUserId = getLastActiveUserId();
        if (lastUserId && lastUserId !== data.user.id) {
          console.log('🔄 Login: Session conflict detected, clearing previous user data...');
          console.log(`🔄 Login: Previous user: ${lastUserId}, New user: ${data.user.id}`);
          resetAllStores();
        }

        const userData = {
          id: data.user.id,
          email: data.user.email,
          firstName: data.user.firstName || data.user.name?.split(' ')[0] || '',
          lastName: data.user.lastName || data.user.name?.split(' ').slice(1).join(' ') || '',
          name: data.user.name,
          contact: data.user.contact || '',
          profilePhoto: profilePhoto,
          role: data.user.role || 'user',
          createdAt: data.user.created_at || new Date().toISOString(),
          flag_daily_update: data.user.flag_daily_update,
          is_super_admin: data.user.is_super_admin || 0,
          is_manager: data.user.is_manager || 0,
          department_id: data.user.department_id || null
        };

        login(userData, data.token, data.refreshToken);

        console.log('✅ Login: Login successful with real user:', {
          id: userData.id,
          name: userData.name,
          email: userData.email,
          profilePhoto: userData.profilePhoto,
          contact: userData.contact
        });
        console.log('🔑 Login: Token received:', data.token ? 'Present' : 'Missing');
        setIsLoggedIn(true);
        // Navigation will happen via useEffect when auth state updates
      } else {
        // Login failed
        console.error('❌ Login: Login failed:', data);
        setError(data.message || 'Login failed. Please check your credentials.');
        setIsLoggingIn(false);
        setAuthenticationStarted(false);
      }
    } catch (error) {
      console.error('❌ Login: Login error:', error);
      // Error message is already formatted by httpClient
      setError(error instanceof Error ? error.message : 'Network error. Please check your connection.');
      setIsLoggingIn(false);
      setAuthenticationStarted(false);
    }
  };


  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // If authentication has started (logging in, logged in, or navigating), show loading
  if (isLoggingIn || isLoggedIn || authenticationStarted) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(33, 150, 243, 0.3)',
            borderTop: '3px solid #2196F3',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <div>{isLoggingIn ? 'Signing in...' : isLoggedIn ? 'Redirecting to dashboard...' : 'Authenticating...'}</div>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }


  // Show login form
  return (
    <>
      <ThemeToggle />
      <AuthCard
        subtitle="Nucleus"
        title="Sign In"
        description="Welcome back! Please sign in to your account."
        maxWidth="md"
      >
        {error && (
          <CustomAlert type="error" message={error} sx={{ mb: 2 }} />
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: '1.5rem' }}>
          <CustomTextField
            type="email"
            placeholder="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={isLoading}
            required
          />

          <CustomTextField
            type="password"
            placeholder="Password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            disabled={isLoading}
            required
            showPasswordToggle
            onTogglePassword={handleTogglePasswordVisibility}
            showPassword={showPassword}
          />

          <Box textAlign="right" sx={{ mb: '1rem', mt: '0.5rem' }}>
            <CustomLink onClick={() => navigate('/reset-password')}>
              Forgot Password?
            </CustomLink>
          </Box>

          <PrimaryButton
            type="submit"
            loading={isLoading}
            fullWidth
            variant="primary"
            sx={{
              mt: '1rem',
              py: '16px',
              px: '32px',
              backgroundColor: '#2196F3', // Solid blue background like in image
              color: 'white', // White text for high contrast
              border: 'none',
              '&:hover': {
                backgroundColor: '#1976D2', // Darker blue on hover
                transform: 'none',
                boxShadow: 'none'
              },
              '&:focus': {
                outline: 'none',
                boxShadow: 'none'
              },
              '&:focus-visible': {
                outline: 'none',
                boxShadow: 'none'
              },
              '&:active': {
                backgroundColor: '#1565C0', // Even darker blue on active
                transform: 'none'
              },
              '&:disabled': {
                backgroundColor: '#BDBDBD', // Gray when disabled
                color: 'white',
                border: 'none',
              }
            }}
          >
            Sign In
          </PrimaryButton>
        </Box>
      </AuthCard>
    </>
  );
};

export default Login;
